import Color from 'colorjs.io';
import { shuffle } from 'lodash';

import { COLOR_PALETTE, ColorToken, HEXColor, isColorToken } from '@neptune/colors-domain';

import { countUsedColors, getNextColor } from './color-palette';

const COLORS_OKLAB_CACHE = new WeakMap<unknown[], Map<ColorToken, Color>>();

export function getColorTokensWithClustering(
  colors: Array<HEXColor | ColorToken | string>,
  n = 2,
): ColorToken[] {
  const size = COLOR_PALETTE.length;

  if (n > size) {
    return randomColorTokens(n, COLOR_PALETTE);
  }

  const usedColorsCounted = countUsedColors(colors);
  let issuedTokens = new Set<ColorToken>();

  const result: ColorToken[] = [];
  let allTokensUsed = false;

  while (result.length < n) {
    if (issuedTokens.size > size && !allTokensUsed) {
      allTokensUsed = true;
      issuedTokens = new Set(result);
    }

    const color = getNextColor(usedColorsCounted, shuffle);

    usedColorsCounted.set(color, (usedColorsCounted.get(color) || 0) + 1);
    issuedTokens.add(color[0]);
    result.push(color[0]);
  }

  return result;
}

export function getColorTokensWithDeltaE(
  colors: Array<HEXColor | ColorToken>,
  n = 2,
): ColorToken[] {
  const size = COLOR_PALETTE.length;

  if (n > size) {
    return randomColorTokens(n, COLOR_PALETTE);
  }

  let availableColors = COLORS_OKLAB_CACHE.get(COLOR_PALETTE);

  if (!availableColors?.size) {
    availableColors = new Map(
      COLOR_PALETTE.map(([token, hex]) => [token, new Color(hex).to('Oklab')]),
    );
    COLORS_OKLAB_CACHE.set(COLOR_PALETTE, availableColors);
  }

  let issuedTokens = new Map<ColorToken, Color>();

  if (colors.length) {
    colors.forEach((color) => {
      if (isColorToken(color)) {
        issuedTokens.set(color, availableColors.get(color) as Color);
      }
    });
  }

  if (!issuedTokens.size) {
    const [token] = COLOR_PALETTE[Math.floor(Math.random() * size)];
    issuedTokens.set(token, availableColors.get(token) as Color);
  }

  const result: ColorToken[] = [];
  let allTokensUsed = false;

  while (result.length < n) {
    if (issuedTokens.size >= size && !allTokensUsed) {
      allTokensUsed = true;
      issuedTokens = new Map(result.map((token) => [token, availableColors.get(token) as Color]));
    }

    const [bestColor] = findBestColors(issuedTokens, availableColors);

    issuedTokens.set(bestColor, availableColors.get(bestColor) as Color);
    result.push(bestColor);
  }

  return result;
}

function randomColorTokens(n = 2, palette = COLOR_PALETTE): ColorToken[] {
  const result = [];
  const source = shuffle(palette);
  const size = source.length;

  for (let i = 0; i < n; i++) {
    result.push(source[i % size][0]);
  }

  return result;
}

function findBestColors(
  currentColors: Color[] | Map<ColorToken, Color>,
  availableColors: Map<ColorToken, Color>,
): ColorToken[] {
  const distances: Array<[ColorToken, number]> = [];

  availableColors.forEach((availableColor, token) => {
    let minDistance = Infinity;
    currentColors.forEach((currentColor) => {
      if (!currentColor) {
        return;
      }

      const distance = currentColor.deltaEOK(availableColor);

      if (distance < minDistance) {
        minDistance = distance;
      }
    });

    distances.push([token, minDistance]);
  });

  distances.sort(([, a], [, b]) => b - a);

  return distances.map(([token]) => token);
}
